<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type Lenis from 'lenis'

const { t } = useI18n()
const localePath = useLocalePath()
const { $lenis } = useNuxtApp() as unknown as { $lenis?: Lenis }

const activeTab = ref('bookings')
const gsapActive = ref(false)

const featureTabs = [
  { id: 'bookings', icon: 'i-approved-bill', labelKey: 'features.tabs.bookings' },
  { id: 'financial', icon: 'i-approved-calculator', labelKey: 'features.tabs.financial' },
  { id: 'human_resources', icon: 'i-approved-comb-scissors', labelKey: 'features.tabs.human_resources' },
  { id: 'catalog', icon: 'i-approved-comb-scissors', labelKey: 'features.tabs.catalog' },
  { id: 'marketing', icon: 'i-approved-reception', labelKey: 'features.tabs.marketing' },
]

type FeatureCard = {
  titleKey: string
  descKey: string
  illustration: string
  border: string
  shadow: string
  iconBorder: string
  iconShadow: string
  icon: string
  iconColor: string
}

// ── Bookings ────────────────────────────────────────────────────────────────
const appointmentSchedulingCard: FeatureCard = {
  titleKey: 'features.frontdesk.card1_title',
  descKey: 'features.frontdesk.card1_desc',
  illustration: '/overview/appointment-scheduling.svg',
  border: 'border-cheese-yellow',
  shadow: 'shadow-[6px_6px_0_#FDC02D]',
  iconBorder: 'border-cheese-yellow',
  iconShadow: 'shadow-[3px_3px_0_#FDC02D]',
  icon: 'i-heroicons-calendar-days-20-solid',
  iconColor: 'text-cheese-yellow',
}

const invoicingPosCard: FeatureCard = {
  titleKey: 'features.frontdesk.card2_title',
  descKey: 'features.frontdesk.card2_desc',
  illustration: '/overview/invoicing-pos.svg',
  border: 'border-heart-purple',
  shadow: 'shadow-[6px_6px_0_#CB2097]',
  iconBorder: 'border-heart-purple',
  iconShadow: 'shadow-[3px_3px_0_#CB2097]',
  icon: 'i-heroicons-credit-card-20-solid',
  iconColor: 'text-heart-purple',
}

const onlineBookingCard: FeatureCard = {
  titleKey: 'features.frontdesk.card3_title',
  descKey: 'features.frontdesk.card3_desc',
  illustration: '/overview/new_booking.svg',
  border: 'border-sky-blue',
  shadow: 'shadow-[6px_6px_0_#2E83FB]',
  iconBorder: 'border-sky-blue',
  iconShadow: 'shadow-[3px_3px_0_#2E83FB]',
  icon: 'i-heroicons-clock-20-solid',
  iconColor: 'text-sky-blue',
}

const staffManagementCard: FeatureCard = {
  titleKey: 'features.frontdesk.card4_title',
  descKey: 'features.frontdesk.card4_desc',
  illustration: '/overview/employees_list.svg',
  border: 'border-cheese-yellow',
  shadow: 'shadow-[6px_6px_0_#FDC02D]',
  iconBorder: 'border-cheese-yellow',
  iconShadow: 'shadow-[3px_3px_0_#FDC02D]',
  icon: 'i-heroicons-users-20-solid',
  iconColor: 'text-cheese-yellow',
}

// ── Financial ────────────────────────────────────────────────────────────────
const revenueReportsCard: FeatureCard = {
  titleKey: 'features.accountant.card1_title',
  descKey: 'features.accountant.card1_desc',
  illustration: '/overview/report.svg',
  border: 'border-cheese-yellow',
  shadow: 'shadow-[6px_6px_0_#FDC02D]',
  iconBorder: 'border-cheese-yellow',
  iconShadow: 'shadow-[3px_3px_0_#FDC02D]',
  icon: 'i-heroicons-chart-bar-20-solid',
  iconColor: 'text-cheese-yellow',
}

const depositsPaymentsCard: FeatureCard = {
  titleKey: 'features.accountant.card2_title',
  descKey: 'features.accountant.card2_desc',
  illustration: '/overview/new_sale.svg',
  border: 'border-heart-purple',
  shadow: 'shadow-[6px_6px_0_#CB2097]',
  iconBorder: 'border-heart-purple',
  iconShadow: 'shadow-[3px_3px_0_#CB2097]',
  icon: 'i-heroicons-banknotes-20-solid',
  iconColor: 'text-heart-purple',
}

const staffCommissionsCard: FeatureCard = {
  titleKey: 'features.accountant.card3_title',
  descKey: 'features.accountant.card3_desc',
  illustration: '/overview/employee_profile.svg',
  border: 'border-sky-blue',
  shadow: 'shadow-[6px_6px_0_#2E83FB]',
  iconBorder: 'border-sky-blue',
  iconShadow: 'shadow-[3px_3px_0_#2E83FB]',
  icon: 'i-heroicons-currency-dollar-20-solid',
  iconColor: 'text-sky-blue',
}

const expenseTrackingCard: FeatureCard = {
  titleKey: 'features.accountant.card4_title',
  descKey: 'features.accountant.card4_desc',
  illustration: '/overview/dashboard.svg',
  border: 'border-cheese-yellow',
  shadow: 'shadow-[6px_6px_0_#FDC02D]',
  iconBorder: 'border-cheese-yellow',
  iconShadow: 'shadow-[3px_3px_0_#FDC02D]',
  icon: 'i-heroicons-receipt-percent-20-solid',
  iconColor: 'text-cheese-yellow',
}

// ── Human Resources ──────────────────────────────────────────────────────────
const inventoryManagementCard: FeatureCard = {
  titleKey: 'features.professional.card1_title',
  descKey: 'features.professional.card1_desc',
  illustration: '/overview/product_details.svg',
  border: 'border-cheese-yellow',
  shadow: 'shadow-[6px_6px_0_#FDC02D]',
  iconBorder: 'border-cheese-yellow',
  iconShadow: 'shadow-[3px_3px_0_#FDC02D]',
  icon: 'i-heroicons-cube-20-solid',
  iconColor: 'text-cheese-yellow',
}

const multiBranchCard: FeatureCard = {
  titleKey: 'features.professional.card2_title',
  descKey: 'features.professional.card2_desc',
  illustration: '/overview/locations.svg',
  border: 'border-heart-purple',
  shadow: 'shadow-[6px_6px_0_#CB2097]',
  iconBorder: 'border-heart-purple',
  iconShadow: 'shadow-[3px_3px_0_#CB2097]',
  icon: 'i-heroicons-building-office-2-20-solid',
  iconColor: 'text-heart-purple',
}

const staffShiftsCard: FeatureCard = {
  titleKey: 'features.professional.card3_title',
  descKey: 'features.professional.card3_desc',
  illustration: '/overview/staff-shifts.svg',
  border: 'border-sky-blue',
  shadow: 'shadow-[6px_6px_0_#2E83FB]',
  iconBorder: 'border-sky-blue',
  iconShadow: 'shadow-[3px_3px_0_#2E83FB]',
  icon: 'i-heroicons-table-cells-20-solid',
  iconColor: 'text-sky-blue',
}

const servicesCatalogCard: FeatureCard = {
  titleKey: 'features.professional.card4_title',
  descKey: 'features.professional.card4_desc',
  illustration: '/overview/services.svg',
  border: 'border-cheese-yellow',
  shadow: 'shadow-[6px_6px_0_#FDC02D]',
  iconBorder: 'border-cheese-yellow',
  iconShadow: 'shadow-[3px_3px_0_#FDC02D]',
  icon: 'i-heroicons-list-bullet-20-solid',
  iconColor: 'text-cheese-yellow',
}

// ── Marketing ────────────────────────────────────────────────────────────────
const clientProfilesCard: FeatureCard = {
  titleKey: 'features.client.card1_title',
  descKey: 'features.client.card1_desc',
  illustration: '/overview/client_profile.svg',
  border: 'border-cheese-yellow',
  shadow: 'shadow-[6px_6px_0_#FDC02D]',
  iconBorder: 'border-cheese-yellow',
  iconShadow: 'shadow-[3px_3px_0_#FDC02D]',
  icon: 'i-heroicons-user-circle-20-solid',
  iconColor: 'text-cheese-yellow',
}

const giftCardsMembershipsCard: FeatureCard = {
  titleKey: 'features.client.card2_title',
  descKey: 'features.client.card2_desc',
  illustration: '/overview/gift_card.svg',
  border: 'border-heart-purple',
  shadow: 'shadow-[6px_6px_0_#CB2097]',
  iconBorder: 'border-heart-purple',
  iconShadow: 'shadow-[3px_3px_0_#CB2097]',
  icon: 'i-heroicons-gift-20-solid',
  iconColor: 'text-heart-purple',
}

const remindersCard: FeatureCard = {
  titleKey: 'features.client.card3_title',
  descKey: 'features.client.card3_desc',
  illustration: '/overview/reminders.svg',
  border: 'border-sky-blue',
  shadow: 'shadow-[6px_6px_0_#2E83FB]',
  iconBorder: 'border-sky-blue',
  iconShadow: 'shadow-[3px_3px_0_#2E83FB]',
  icon: 'i-heroicons-bell-20-solid',
  iconColor: 'text-sky-blue',
}

const featureCards: Record<string, FeatureCard[]> = {
  bookings: [
    appointmentSchedulingCard,
    onlineBookingCard,
  ],
  financial: [
    invoicingPosCard,
    revenueReportsCard,
    depositsPaymentsCard,
    expenseTrackingCard,
  ],
  human_resources: [
    staffManagementCard,
    staffShiftsCard,
    staffCommissionsCard,
  ],
  catalog: [
    inventoryManagementCard,
    multiBranchCard,
    servicesCatalogCard,
  ],
  marketing: [
    remindersCard,
    giftCardsMembershipsCard,
    clientProfilesCard,
  ],
}

// Cumulative start index per tab so card numbers run 01–20 across all sections
const cardGlobalOffset = computed(() => {
  let offset = 0
  const result: Record<string, number> = {}
  for (const tab of featureTabs) {
    result[tab.id] = offset
    offset += featureCards[tab.id]?.length ?? 0
  }
  return result
})

// Flat list of every card across all tabs, in tab order. On mobile we render
// ALL of these in a single slider (rather than swapping the set per tab); each
// entry keeps its owning tab so sliding can drive the active tab, plus a 1-based
// global number for the card badge.
const allCards = computed(() => {
  const result: { card: FeatureCard, tabId: string, number: number }[] = []
  let n = 0
  for (const tab of featureTabs) {
    for (const card of featureCards[tab.id] ?? []) {
      result.push({ card, tabId: tab.id, number: ++n })
    }
  }
  return result
})

const section = ref<HTMLElement | null>(null)
const pinTarget = ref<HTMLElement | null>(null)
const cardsOuter = ref<HTMLElement | null>(null)
const cardsTrack = ref<HTMLElement | null>(null)
const dirX = useDirX()
const isRtl = computed(() => dirX.value === -1)

// Mobile slider: hold a handle on the Swiper so tab clicks can jump to a tab's
// first card, and let sliding drive which tab reads as active.
type FeatSwiper = { slideTo: (i: number, speed?: number) => void, activeIndex: number }
const featSwiper = ref<FeatSwiper | null>(null)
const onFeatSwiper = (swiper: FeatSwiper) => {
  featSwiper.value = swiper
}
const onFeatSlideChange = (swiper: FeatSwiper) => {
  const tabId = allCards.value[swiper.activeIndex]?.tabId
  if (tabId && activeTab.value !== tabId) activeTab.value = tabId
}

let pinST: ReturnType<typeof ScrollTrigger.create> | null = null
const _sts: ReturnType<typeof ScrollTrigger.create>[] = []
let _tabsEntranceDone = false
// Store computed layout values for jumpToTab()
let _maxTravel = 0
let _scrollDist = 0
let _cardStep = 0
let _tabCardPixels: number[] = []
let _gap = 28

function jumpToTab(tabId: string) {
  const tabIndex = featureTabs.findIndex(tab => tab.id === tabId)
  if (tabIndex === -1) return

  if (gsapActive.value && pinST) {
    // Scroll to the exact position where this tab's first card aligns to the track's left edge
    const tabTrackX = _tabCardPixels[tabIndex] ?? 0
    const targetProgress = _maxTravel > 0 ? Math.min(tabTrackX / _maxTravel, 1) : 0
    const targetScrollY = pinST.start + targetProgress * _scrollDist
    window.scrollTo({ top: targetScrollY, behavior: 'smooth' })
    return
  }

  // Mobile: slide the unified carousel to this tab's first card; the slide-change
  // handler then syncs activeTab.
  featSwiper.value?.slideTo(cardGlobalOffset.value[tabId] ?? 0)
}

watch(activeTab, (newId) => {
  if (!_tabsEntranceDone) return
  nextTick(() => {
    const btn = section.value?.querySelector<HTMLElement>(`[data-tab-id="${newId}"]`)
    if (btn) {
      gsap.fromTo(btn, { scale: 0.88 }, { scale: 1, duration: 0.4, ease: 'back.out(2.2)', overwrite: true })
    }
  })
})

onMounted(async () => {
  gsapActive.value = window.innerWidth >= 1024
  await nextTick()

  const el = section.value
  if (!el) return

  // Heading entrance (fires before pin kicks in)
  const headingTween = gsap.fromTo(el.querySelector('.feat-heading'),
    { opacity: 0, y: 50 },
    {
      opacity: 1, y: 0, duration: 0.85, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 78%', once: true },
    },
  )
  if (headingTween.scrollTrigger) _sts.push(headingTween.scrollTrigger)

  const tabsTween = gsap.fromTo(el.querySelectorAll('.feat-tab'),
    { opacity: 0, y: 30, scale: 0.88 },
    {
      opacity: 1, y: 0, scale: 1, duration: 0.55, ease: 'back.out(1.5)', stagger: 0.09,
      onComplete: () => { _tabsEntranceDone = true },
      scrollTrigger: { trigger: el.querySelector('.feat-tabs'), start: 'top 84%', once: true },
    },
  )
  if (tabsTween.scrollTrigger) _sts.push(tabsTween.scrollTrigger)

  if (!gsapActive.value) {
    // Mobile: original card batch animations + tab carousel
    const batchSTs = ScrollTrigger.batch(el.querySelectorAll('.feat-card'), {
      interval: 0.1, batchMax: 2, start: 'top 90%',
      onEnter: batch =>
        gsap.fromTo(batch,
          { opacity: 0, y: 70, scale: 0.88, rotation: -3 },
          { opacity: 1, y: 0, scale: 1, rotation: 0, duration: 0.7, ease: 'back.out(1.4)', stagger: 0.1, overwrite: true },
        ),
      once: true,
    })
    _sts.push(...batchSTs)
    const mobileCtaTween = gsap.fromTo(el.querySelector('.feat-cta'),
      { opacity: 0, y: 30 },
      {
        opacity: 1, y: 0, duration: 0.7, ease: 'power3.out',
        scrollTrigger: { trigger: el.querySelector('.feat-cta'), start: 'top 92%', once: true },
      },
    )
    if (mobileCtaTween.scrollTrigger) _sts.push(mobileCtaTween.scrollTrigger)
    ScrollTrigger.refresh()
    return
  }

  // Desktop: flat-track + pin. Template already renders this mode (gsapActive
  // was set in setup), so the DOM is final — no nextTick / mode swap needed.
  const pin = pinTarget.value
  const outer = cardsOuter.value
  const track = cardsTrack.value
  if (!pin || !outer || !track) return

  // Read real card dimensions from the rendered DOM
  const firstCard = track.querySelector<HTMLElement>('.feat-card')
  if (!firstCard) return

  // The clip box (outer) spans the full viewport so cards bleed off both screen
  // edges mid-scroll. Pad the track back to the container's content edges so the
  // FIRST card starts aligned to the container and the LAST card rests fully in
  // view (aligned to the container's end edge) rather than flush to the screen
  // edge. Gutters are measured from the parent .container's rect + its inline
  // padding; in RTL start/end swap sides. Must run before reading scrollWidth so
  // _maxTravel includes both pads.
  // Start side uses padding-inline-start (reliably counted in scrollWidth). The
  // end side does NOT use padding: browsers omit end/right padding from
  // scrollWidth, so we instead fold the end gutter into _maxTravel below so the
  // track scrolls far enough to land the last card at the container's end edge.
  let endGutter = 0
  const containerEl = outer.parentElement
  if (containerEl) {
    const cRect = containerEl.getBoundingClientRect()
    const cs = getComputedStyle(containerEl)
    const leftGutter = cRect.left + parseFloat(cs.paddingLeft || '0')
    const rightGutter = (window.innerWidth - cRect.right) + parseFloat(cs.paddingRight || '0')
    track.style.paddingInlineStart = `${isRtl.value ? rightGutter : leftGutter}px`
    endGutter = isRtl.value ? leftGutter : rightGutter
  }

  _gap = Number(getComputedStyle(track).columnGap.replace('px', '')) || 28
  _cardStep = firstCard.offsetWidth + _gap
  // Cumulative card starts per tab
  let runningCards = 0
  const tabCardStarts = featureTabs.map((tab) => {
    const start = runningCards
    runningCards += featureCards[tab.id]?.length ?? 0
    return start
  })
  const totalCards = runningCards

  _maxTravel = track.scrollWidth - outer.clientWidth + endGutter
  _scrollDist = totalCards * 260

  // Scale thresholds proportionally within [0, _maxTravel] so every tab is reachable.
  // Raw pixel offsets (N × _cardStep) exceed _maxTravel on shorter tracks, making the
  // last tab(s) permanently unreachable — this proportional mapping avoids that.
  _tabCardPixels = tabCardStarts.map(start => (start / totalCards) * _maxTravel)

  // In RTL, flex children render right→left so card 1 already sits on the visual
  // right. The track must translate in the opposite direction to reveal later
  // cards. dirX flips the sign so progress 0→1 always cycles tabs in source order.
  const tl = gsap.timeline()
  tl.to(track, { x: -_maxTravel * dirX.value, ease: 'none' })

  // CTA fades in as the section enters view (it now lives inside the pin, so we
  // trigger off the section itself rather than the CTA's natural position).
  const desktopCtaTween = gsap.fromTo(el.querySelector('.feat-cta'),
    { opacity: 0, y: 20 },
    {
      opacity: 1, y: 0, duration: 0.8, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 70%', once: true },
    },
  )
  if (desktopCtaTween.scrollTrigger) _sts.push(desktopCtaTween.scrollTrigger)

  pinST = ScrollTrigger.create({
    trigger: pin,
    start: 'top top',
    end: `+=${_scrollDist}`,
    pin: true,
    scrub: 1.2,
    anticipatePin: 1,
    animation: tl,
    // On refresh, this pin MUST run BEFORE post-pin triggers so its spacer is
    // inserted before they recalculate start positions. ScrollTrigger sorts by
    // refreshPriority descending — HIGHER refreshes first. Default is 0, so 1
    // puts this pin ahead of the rest.
    refreshPriority: 1,
    onUpdate: (self) => {
      const currentX = self.progress * _maxTravel
      const tabIndex = Math.max(0, _tabCardPixels.findLastIndex(px => currentX >= px))
      const newTabId = featureTabs[tabIndex]?.id
      if (newTabId && activeTab.value !== newTabId) {
        activeTab.value = newTabId
      }
    },
  })

  // The pin spacer GSAP inserts pushes every section below Features down by ~_scrollDist px.
  // Lenis caches scrollMax against the old document height — without resize() it would
  // hard-stop scroll before the user could reach (or pass through) the pin.
  // Refresh recalculates all triggers against the new layout.
  $lenis?.resize()
  ScrollTrigger.refresh()

  // Re-run once images/fonts have settled, in case they shifted layout post-mount.
  if (document.readyState !== 'complete') {
    window.addEventListener('load', () => {
      $lenis?.resize()
      ScrollTrigger.refresh()
    }, { once: true })
  }
})

// Scroll-driven wavy line phases — static when idle, mirrors away from button on scroll
const _wavyScrollY = ref(0)
const WAVY_FACTOR = 0.15
let _wavyRaf: number | null = null

function _onWavyScroll() {
  if (_wavyRaf) return
  _wavyRaf = requestAnimationFrame(() => {
    _wavyScrollY.value = window.scrollY
    _wavyRaf = null
  })
}

// Start line (left in LTR, right in RTL) expands toward its own edge on scroll down
const wavyStartPhase = computed(() => _wavyScrollY.value * WAVY_FACTOR * (isRtl.value ? 1 : -1))
const wavyEndPhase = computed(() => -wavyStartPhase.value)

onMounted(() => {
  window.addEventListener('scroll', _onWavyScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', _onWavyScroll)
  if (_wavyRaf) cancelAnimationFrame(_wavyRaf)
  _sts.forEach(st => st.kill())
  _sts.length = 0
  pinST?.kill()
  pinST = null
  _tabsEntranceDone = false
  const el = section.value
  if (el) {
    gsap.killTweensOf(el.querySelectorAll('.feat-heading, .feat-tab, .feat-cta, .feat-card'))
  }
})
</script>

<template>
  <section id="features" ref="section" class="bg-amber-50">
    <!-- Pinned wrapper: heading + tabs + cards + CTA all inside, so the pinned
         viewport has no empty gaps. Content fills via flex-1, CTA sits at bottom. -->
    <div
      ref="pinTarget"
      :class="[
        'feat-pin flex flex-col',
        gsapActive ? 'h-screen pt-24 pb-10 lg:pt-28 lg:pb-14' : 'pt-24 pb-12 md:pt-28 md:pb-16 lg:pt-32 lg:pb-20',
      ]">
      <!-- flex-1 + min-h-0: fills the space above the CTA and can shrink below
           natural content height. justify-center: vertically centers the
           heading+tabs+track block within the available space on tall screens. -->
      <div class="container flex-1 min-h-0 flex flex-col justify-center">

        <!-- Heading -->
        <div class="feat-heading text-center mb-8 md:mb-10">
          <i18n-t
            keypath="features.heading"
            tag="h2"
            class="text-[clamp(1.75rem,3.5vw,3rem)] font-bold leading-[1.25] text-gray-900 mb-3">
            <template #manage>
              <span class="text-furious-purple">{{ t('features.manage') }}</span>
            </template>
            <template #book>
              <span class="text-sky-blue">{{ t('features.book') }}</span>
            </template>
          </i18n-t>
          <p class="feat-subheading text-base md:text-lg text-gray-900 max-w-3xl mx-auto">{{ t('features.subheading') }}</p>
        </div>

        <!-- Tabs — active gets a neubrutalism fill (white bg + offset shadow)
             directly on the button, so there's no sliding-indicator math to get
             wrong in RTL. -->
        <div class="feat-tabs grid grid-cols-2 gap-2.5 self-stretch md:flex md:flex-wrap md:self-center md:justify-center md:gap-3 mb-10 md:mb-12" role="tablist" aria-label="Feature categories">
          <button
            v-for="tab in featureTabs"
            :key="tab.id"
            :data-tab-id="tab.id"
            :class="['feat-tab bfm-btn bfm-btn-sm flex-col md:flex-row !h-auto md:!h-10 gap-1 md:gap-1.5 !px-2 md:!px-4 py-2 md:py-0 !text-xs md:!text-sm !whitespace-normal md:!whitespace-nowrap text-center leading-tight rounded-lg transition-all duration-300 ease-out',
              activeTab === tab.id
                ? 'bg-white border-gray-900 text-gray-900 shadow-[3px_3px_0_#111827]'
                : 'border-gray-300 text-overcast-night']"
            role="tab"
            :aria-selected="activeTab === tab.id"
            @click="jumpToTab(tab.id)">
            {{ t(tab.labelKey) }}
          </button>
        </div>

        <!-- ── Mobile (< md): one continuous Swiper carousel holding EVERY card
             across all tabs. Sliding drives the active tab (via slide-change),
             and tapping a tab jumps to that tab's first card. Bleeds to the
             screen end so the active card runs nearly full-width while the next
             card peeks; re-keyed only on direction so RTL rebuilds cleanly.
             overflow-x-clip on the wrapper contains the peek without clipping
             the cards' vertical shadows. -->
        <div v-if="!gsapActive" class="me-[calc(50%_-_50vw)] overflow-x-clip pt-2">
          <Swiper
            :key="isRtl ? 'rtl' : 'ltr'"
            :dir="isRtl ? 'rtl' : 'ltr'"
            slides-per-view="auto"
            :space-between="16"
            :slides-offset-before="16"
            :slides-offset-after="16"
            :auto-height="true"
            :grab-cursor="true"
            :modules="[SwiperPagination]"
            :pagination="{ clickable: true, dynamicBullets: true }"
            class="feat-swiper !overflow-visible"
            role="tabpanel"
            @swiper="onFeatSwiper"
            @slide-change="onFeatSlideChange">
            <SwiperSlide
              v-for="entry in allCards"
              :key="entry.card.titleKey"
              class="!h-auto !w-[85vw] max-w-[20rem]">
              <div :class="['feat-card bg-white rounded-2xl border-2 border-gray-900 overflow-hidden flex flex-col', entry.card.shadow]">
                <div class="flex items-start justify-between p-5 pb-2">
                  <div :class="['size-13 rounded-xl border flex items-center justify-center bg-white border-black', entry.card.iconShadow]">
                    <UIcon :name="entry.card.icon" :class="['size-6', entry.card.iconColor]" />
                  </div>
                  <span class="text-6xl font-black text-gray-100 leading-none select-none">{{ String(entry.number).padStart(2, '0') }}</span>
                </div>
                <div class="px-5 pb-4">
                  <h3 class="text-base font-bold text-gray-900 mb-1.5 line-clamp-2">{{ t(entry.card.titleKey) }}</h3>
                  <p class="text-sm text-overcast-night leading-relaxed line-clamp-3">{{ t(entry.card.descKey) }}</p>
                </div>
                <div class="mt-2 px-4 pt-1 pb-4">
                  <img :src="entry.card.illustration" :alt="t(entry.card.titleKey)" class="w-full aspect-[16/9] object-contain" loading="lazy">
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>

        <!-- ── Desktop: GSAP-driven flat track ──
             Full-bleed clip box (mx negative gutter → 100vw wide) so cards slide
             off BOTH screen edges during scroll. The first card is offset back to
             the container's content edge via a JS-measured leading pad, so cards
             *start* aligned to the container but the container only constrains the
             initial frame. -->
        <div v-else ref="cardsOuter" class="mx-[calc(50%_-_50vw)] overflow-hidden flex-1 min-h-0 max-h-[26rem]">
          <div ref="cardsTrack" class="flex gap-6 md:gap-7 h-full pt-2 pb-6 will-change-transform">
            <template v-for="tab in featureTabs" :key="tab.id">
              <div
                v-for="(card, i) in featureCards[tab.id]"
                :key="card.titleKey"
                :class="['feat-card h-full shrink-0 w-72 md:w-80 bg-white rounded-2xl border-2 border-gray-900 overflow-hidden flex flex-col transition-transform duration-150 hover:-translate-x-[3px] hover:-translate-y-[3px]', card.shadow]">
                <div class="feat-card-head flex items-start justify-between p-5 md:p-6 pb-2">
                  <div :class="['size-13 rounded-xl border flex items-center justify-center bg-white border-black', card.iconShadow]">
                    <UIcon :name="card.icon" :class="['size-6 md:size-7', card.iconColor]" />
                  </div>
                  <span class="feat-card-number text-6xl md:text-7xl font-black text-gray-100 leading-none select-none">{{ String(cardGlobalOffset[tab.id] + i + 1).padStart(2, '0') }}</span>
                </div>
                <div class="px-5 md:px-6 pb-4">
                  <h3 class="text-base md:text-lg font-bold text-gray-900 mb-1.5 line-clamp-2">{{ t(card.titleKey) }}</h3>
                  <p class="feat-card-desc text-sm text-overcast-night leading-relaxed line-clamp-3">{{ t(card.descKey) }}</p>
                </div>
                <div class="feat-card-media flex-1 min-h-0 flex px-4 md:px-5 pt-1 pb-4">
                  <img :src="card.illustration" :alt="t(card.titleKey)" class="w-full h-full object-contain object-bottom rounded-t-xl" loading="lazy">
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- CTA: lives inside the pinned wrapper at the bottom, so the viewport
           reads heading → cards → CTA without any empty filler. -->
      <div class="container mt-6 md:mt-8">
        <div class="feat-cta flex items-center justify-center">
          <CommonWavyLine class="hidden md:inline-block shrink-0" length="120px" :wl="45" :amp="8" :th="2.5" color="#111827" :phase="wavyStartPhase" />
          <NuxtLink :to="localePath('/features')" class="bfm-btn bfm-btn-md md:bfm-btn-lg bg-cheese-yellow whitespace-nowrap">
            {{ t('features.cta') }}
          </NuxtLink>
          <CommonWavyLine class="hidden md:inline-block shrink-0" length="120px" :wl="45" :amp="8" :th="2.5" color="#111827" :phase="wavyEndPhase" />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Short desktop viewports (small laptops, or a wide monitor viewed at 125%+
   scale where innerHeight shrinks). The vertical layout already cannot overflow
   the 100vh pin — cardsOuter is the single flex-grow element and absorbs all
   leftover space, so the CTA is always inside the frame and the card images
   shrink to fit (object-cover) instead of clipping the card or pushing the CTA
   out. These tiers are PURELY cosmetic: they reclaim height from paddings,
   margins, and text so the flexible image area stays as large as possible on
   short screens. Scoped selectors carry the [data-v] attribute, which outranks
   the single-class Tailwind utilities. Card WIDTH is never touched — changing it
   would desync the GSAP _cardStep / _maxTravel measurement taken once at mount. */

/* ── Tier 1 (≤900px) — e.g. 1080p at 120%, or a laptop at 100% ─────────── */
@media (min-width: 1024px) and (max-height: 900px) {
  .feat-pin     { padding-top: 4.5rem; padding-bottom: 2.5rem; }
  .feat-heading { margin-bottom: 0.875rem; }
  .feat-tabs    { margin-bottom: 1.75rem; }
  .feat-card-head { padding: 1rem 1rem 0.5rem; }
}

/* ── Tier 2 (≤780px) — e.g. 1366×768 at 100%, or 1080p at 125%–140% ───── */
@media (min-width: 1024px) and (max-height: 780px) {
  .feat-pin     { padding-top: 4rem; padding-bottom: 2rem; }
  .feat-heading { margin-bottom: 0.5rem; }
  /* Drop the subheading and a description line to give the flexible image area
     more height on short screens. */
  .feat-subheading  { display: none; }
  .feat-card-desc   { -webkit-line-clamp: 2; line-clamp: 2; }
  .feat-tabs    { margin-bottom: 1.5rem; }
  .feat-card-head { padding: 0.875rem 1rem 0.375rem; }
  .feat-card-number { font-size: 3rem; }
}

/* ── Tier 3 (≤650px) — e.g. 768p at 125%+, or 1080p at 150%+ ───────────── */
@media (min-width: 1024px) and (max-height: 650px) {
  .feat-pin     { padding-top: 3.5rem; padding-bottom: 1.75rem; }
  .feat-heading { margin-bottom: 0.25rem; }
  .feat-subheading  { display: none; }
  .feat-card-desc   { -webkit-line-clamp: 2; line-clamp: 2; }
  .feat-tabs    { margin-bottom: 1.25rem; }
  .feat-card-head { padding: 0.625rem 0.875rem 0.25rem; }
  .feat-card-number { font-size: 2.5rem; }
}

/* Carousel dots flow below the cards (static) rather than overlaying them,
   and use the brand palette: inactive = ink, active = heart-purple pill. */
.feat-swiper :deep(.swiper-pagination) {
  position: static;
  display: flex;
  justify-content: center;
  gap: 0.4rem;
  margin-top: 0.875rem;
}

.feat-swiper :deep(.swiper-pagination-bullet) {
  width: 8px;
  height: 8px;
  margin: 0 !important;
  background: #111827;
  opacity: 0.2;
  transition: width 0.25s ease, opacity 0.25s ease, background 0.25s ease;
}

.feat-swiper :deep(.swiper-pagination-bullet-active) {
  width: 1.5rem;
  border-radius: 9999px;
  opacity: 1;
  background: #cb2097;
}
</style>
